import {
  ImageProcessingError,
  supportsImageRequest,
  type ImageRequest,
  type ImageResult,
  type ImageWorkerRequest,
  type ImageWorkerResponse,
} from './image-processing';

interface PendingRequest {
  readonly resolve: (result: ImageResult) => void;
  readonly reject: (error: ImageProcessingError) => void;
}

let imageWorker: Worker | undefined;
let nextRequestId = 0;
const pendingRequests = new Map<number, PendingRequest>();

const failPendingRequests = (error: ImageProcessingError): void => {
  for (const request of pendingRequests.values()) {
    request.reject(error);
  }
  pendingRequests.clear();
};

const getImageWorker = (): Worker => {
  if (imageWorker) {
    return imageWorker;
  }
  if (typeof Worker === 'undefined') {
    throw new ImageProcessingError('worker-unavailable', 'Image processing is only available in a browser.');
  }

  try {
    imageWorker = new Worker(new URL('./image-processing.worker', import.meta.url), { type: 'module' });
  } catch (error) {
    throw new ImageProcessingError('worker-failed', 'Unable to start the image-processing Worker.', error);
  }

  imageWorker.onmessage = ({ data }: MessageEvent<ImageWorkerResponse>) => {
    const request = pendingRequests.get(data.id);
    if (!request) {
      return;
    }

    pendingRequests.delete(data.id);
    if ('result' in data) {
      request.resolve(data.result);
    } else {
      request.reject(new ImageProcessingError(data.error.code, data.error.message));
    }
  };
  imageWorker.onerror = ({ message }: ErrorEvent) => {
    failPendingRequests(new ImageProcessingError('worker-failed', message || 'The image-processing Worker failed.'));
    imageWorker?.terminate();
    imageWorker = undefined;
  };

  return imageWorker;
};

export const processImage = (request: ImageRequest): Promise<ImageResult> => {
  if (!supportsImageRequest(request)) {
    return Promise.reject(new ImageProcessingError('unsupported-request', 'The image request is not supported.'));
  }

  return new Promise((resolve, reject) => {
    const id = nextRequestId++;
    pendingRequests.set(id, { resolve, reject });

    try {
      const message: ImageWorkerRequest = { id, request };
      getImageWorker().postMessage(message, [request.bytes]);
    } catch (error) {
      pendingRequests.delete(id);
      reject(
        error instanceof ImageProcessingError
          ? error
          : new ImageProcessingError('worker-failed', 'Unable to send the image request to the Worker.', error)
      );
    }
  });
};
